import { AdminUrls, CloudActions, FlowjoCommons } from '@portal/portal-core';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  fetchError,
  fetchSuccess,
  setAllCostCenterInvites,
  setCostCenterDetails,
  setCostCenterInvites,
  setCostCenterInvitesCache,
  setCostCenterInvitesCacheFetchError,
  setCostCenterInvitesFetchError,
} from 'actions/super-admin';
// Import via the `constants/*` path alias (bare 'constants' resolves to Node's built-in module in TS).
import * as Constants from 'constants/index';
import { AdminClientUtils } from 'ducks/admin-client';
import { SuperAdminActions } from 'ducks/super-client';

// Boundary shape of the admin client config threaded through these sagas.
// `getAuthToken` is forwarded to `AdminClientUtils.sendAction`.
interface SagaConfig {
  baseApiUrl?: string;
  timeout?: number;
  getAuthToken?: () => string | null | undefined;
}

// Redux actions dispatched into these sagas always carry a string `type`.
// Modelling that lets `takeLatest` resolve its pattern+worker+args overload
// (the worker's trailing action param must extend redux-saga's `Action`).
interface SagaAction {
  type: string;
}

// Actions dispatched into the cost-center sagas also carry a cost-center id.
interface CostCenterAction extends SagaAction {
  costCenterId: string;
}

// Result shape returned by AdminClientUtils.sendAction (an untyped `.js` client).
// `data` and `error` stay `any` at this boundary so the original access and
// `error?.message || error` fallback expressions are preserved exactly.
interface SendActionResult {
  succeeded?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}

export function* addIndividualPhantomUserToProduct(
  config: SagaConfig,
  action: SagaAction,
): Generator<unknown, void, unknown> {
  const url = AdminUrls.adminClientPath();
  const { succeeded, error } = (yield call(
    AdminClientUtils.sendAction,
    config,
    action,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(SuperAdminActions.showLicenseCreationSuccess());
  } else {
    const err = error?.message || error;
    yield put(SuperAdminActions.showLicenseCreationError([err]));
  }

  // Preserve the original no-arg runtime call (limit stays undefined).
  yield put(CloudActions.getIndividualLicensesVerbose(undefined));
}

/* istanbul ignore next */
export function* watchAddIndividualPhantomUserToProduct(
  config: SagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    CloudActions.ADMIN_ADD_INDIVIDUAL_PHANTOM_USER_TO_PRODUCT,
    addIndividualPhantomUserToProduct,
    config,
  );
}

export function* fetchCostCenterInvites(
  config: SagaConfig,
  action: CostCenterAction,
): Generator<unknown, void, unknown> {
  const url = AdminUrls.adminClientPath();
  const serverAction = CloudActions.adminGetCostCenterInvites(action.costCenterId);
  const { succeeded, data, error } = (yield call(
    AdminClientUtils.sendAction,
    config,
    serverAction,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(fetchSuccess());
    yield put(setCostCenterInvites(data));
  } else {
    const err = error?.message || error;
    yield put(fetchError([err]));
  }
}

/* istanbul ignore next */
export function* watchFetchCostCenterInvites(
  config: SagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_LICENSE_ROW,
    fetchCostCenterInvites,
    config,
  );
}

export function* fetchCostCenterDetails(
  config: SagaConfig,
  action: CostCenterAction,
): Generator<unknown, void, unknown> {
  const url = AdminUrls.adminClientPath();
  const serverAction = CloudActions.costCenterDetails(action.costCenterId);
  const { succeeded, data, error } = (yield call(
    AdminClientUtils.sendAction,
    config,
    serverAction,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(fetchSuccess());
    yield put(setCostCenterDetails(data.costCenter));
  } else {
    const err = error?.message || error;
    yield put(fetchError([err]));
  }
}

/* istanbul ignore next */
export function* watchFetchCostCenterDetails(
  config: SagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_LICENSE_ROW,
    fetchCostCenterDetails,
    config,
  );
}

export function* fetchAllCostCenterInvites(
  config: SagaConfig,
  action: SagaAction,
): Generator<unknown, void, unknown> {
  const url = AdminUrls.adminClientPath();
  const { succeeded, data, error } = (yield call(
    AdminClientUtils.sendAction,
    config,
    action,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(setAllCostCenterInvites(data));
  } else {
    yield put(setCostCenterInvitesFetchError());
    const { Alerts } = FlowjoCommons;
    const err = error?.message ?? String(error);
    yield put(
      Alerts.AlertActions.setAlert(
        Alerts.Alert.AlertStamp({ id: 'INVITES_FETCH_ERROR', date: null, type: err }),
      ),
    );
  }
}

/* istanbul ignore next */
export function* watchFetchAllCostCenterInvites(
  config: SagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    CloudActions.ADMIN_GET_ALL_ACTIVE_COST_CENTER_INVITES,
    fetchAllCostCenterInvites,
    config,
  );
}

// Reuses the existing cost-center-scoped ADMIN_GET_COST_CENTER_INVITES action/backend handler
// directly (rather than a separate productAccessId-scoped action) - every invite already carries
// its own productAccessId, so a consumer that needs counts for one specific license filters this
// cost center's invites itself instead of the server doing that filtering.
export function* fetchCostCenterInvitesCache(
  config: SagaConfig,
  action: CostCenterAction,
): Generator<unknown, void, unknown> {
  const url = AdminUrls.adminClientPath();
  const { succeeded, data, error } = (yield call(
    AdminClientUtils.sendAction,
    config,
    action,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(setCostCenterInvitesCache(action.costCenterId, data));
  } else {
    yield put(setCostCenterInvitesCacheFetchError(action.costCenterId));
    const { Alerts } = FlowjoCommons;
    const err = error?.message ?? String(error);
    yield put(
      Alerts.AlertActions.setAlert(
        Alerts.Alert.AlertStamp({
          id: 'COST_CENTER_INVITES_CACHE_FETCH_ERROR',
          date: null,
          type: err,
        }),
      ),
    );
  }
}

/* istanbul ignore next */
export function* watchFetchCostCenterInvitesCache(
  config: SagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(CloudActions.ADMIN_GET_COST_CENTER_INVITES, fetchCostCenterInvitesCache, config);
}

export default (config: SagaConfig = {}) =>
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([
      watchAddIndividualPhantomUserToProduct(config),
      watchFetchCostCenterInvites(config),
      watchFetchCostCenterDetails(config),
      watchFetchAllCostCenterInvites(config),
      watchFetchCostCenterInvitesCache(config),
    ]);
  };
